import { useMemo } from 'react'
import { useContacts } from '@/api/contacts'
import type { Contact } from '@/types/contacts'
import { nomContact } from '@/lib/nomContact'

/**
 * Construit un map { phoneNumber → Contact } à partir de la liste des contacts.
 * Indexe sur le numéro tel quel + version sans espaces/+.
 */
function buildContactsMap(contacts: Contact[]): Record<string, Contact> {
  const map: Record<string, Contact> = {}
  for (const contact of contacts) {
    if (!contact.phone_number) continue
    const normalized = contact.phone_number.replace(/\s+/g, '').replace(/^\+/, '')
    map[normalized] = contact
    map[contact.phone_number] = contact
  }
  return map
}

/**
 * Hook pour résoudre rapidement un Contact par numéro de téléphone.
 *
 * Utilise TanStack Query (`useContacts`) pour le cache + dédup automatique.
 * Plusieurs composants qui appellent `useContactsMap()` partagent le même fetch.
 */
export function useContactsMap() {
  const query = useContacts()
  const contacts = query.data || []

  const contactsMap = useMemo(() => buildContactsMap(contacts), [contacts])

  const getContactByNumber = (
    phoneNumber: string | null | undefined,
  ): Contact | null => {
    if (!phoneNumber) return null

    if (contactsMap[phoneNumber]) return contactsMap[phoneNumber]

    const stripped = phoneNumber.replace(/[\s\-()+]/g, '')
    if (contactsMap[stripped]) return contactsMap[stripped]
    if (contactsMap[`+${stripped}`]) return contactsMap[`+${stripped}`]

    // Match sur les 10 derniers chiffres (numéro local sans country code)
    const last10 = stripped.length >= 10 ? stripped.slice(-10) : stripped

    for (const key in contactsMap) {
      const c = contactsMap[key]
      const cStripped = c.phone_number.replace(/[\s\-()+]/g, '')
      if (cStripped === stripped) return c
      const cLast10 = cStripped.length >= 10 ? cStripped.slice(-10) : cStripped
      if (last10 === cLast10 && last10.length >= 10) return c
    }
    return null
  }

  const getDisplayName = (phoneNumber: string | null | undefined): string => {
    return nomContact(getContactByNumber(phoneNumber), phoneNumber || '')
  }

  const getInitials = (phoneNumber: string | null | undefined): string => {
    const contact = getContactByNumber(phoneNumber)
    if (contact?.initials) return contact.initials
    if (contact?.first_name || contact?.last_name) {
      const first = contact.first_name?.[0]?.toUpperCase() || ''
      const last = contact.last_name?.[0]?.toUpperCase() || ''
      const initials = `${first}${last}`
      if (initials) return initials
    }
    if (phoneNumber) {
      const lastTwo = phoneNumber.slice(-2)
      return lastTwo || '?'
    }
    return '?'
  }

  const getAvatarUrl = (phoneNumber: string | null | undefined): string | null => {
    const contact = getContactByNumber(phoneNumber)
    return contact?.avatar_signed_url || contact?.avatar_url || null
  }

  return {
    contactsMap,
    contacts,
    loading: query.isLoading,
    error: query.error?.message || null,
    reloadContacts: () => query.refetch(),
    getContactByNumber,
    getDisplayName,
    getInitials,
    getAvatarUrl,
  }
}
